// GaugeChart — semi-circle radial gauge for displaying a rate (0-1) against a target

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

export interface GaugeChartProps {
  value: number; // 0-1 (e.g. 0.378 for 37.8%)
  target: number; // 0-1 (e.g. 0.55 for 55%)
  label: string; // e.g. "First-Year Retention"
  isLoading?: boolean;
}

function getGaugeColor(value: number, target: number): string {
  if (value >= target) return "#16a34a"; // green — at or above target
  if (value >= target * 0.7) return "#d97706"; // amber — within 30% of target
  return "#dc2626"; // red — well below target
}

export default function GaugeChart({
  value,
  target,
  label,
  isLoading = false,
}: GaugeChartProps) {
  const clamped = Math.min(Math.max(value, 0), 1);
  const gaugeColor = getGaugeColor(clamped, target);

  // PieChart half-circle: startAngle=180, endAngle=0
  // filled portion = clamped * 100%, remainder = rest
  const filled = clamped;
  const empty = 1 - filled;
  const data = [
    { name: "value", value: filled },
    { name: "empty", value: empty },
  ];

  // Target needle angle: maps target (0-1) to degrees in [180, 0]
  // angle = 180 - target * 180
  const targetAngleDeg = 180 - target * 180;
  const _targetRad = (targetAngleDeg * Math.PI) / 180;
  // Needle sits at the midpoint radius of the arc (inner 60%, outer 80% of viewBox)
  // The SVG coordinate space used by Recharts for a centered pie: cx=50%, cy=100%
  // We'll use a custom SVG overlay via the label approach
  const pctDisplay = `${Math.round(clamped * 100)}%`;

  if (isLoading) {
    return (
      <div className="glass rounded-2xl p-5 flex flex-col items-center gap-3">
        <div className="skeleton h-4 w-32 rounded-md" />
        <div
          className="skeleton rounded-full"
          style={{ width: 160, height: 80 }}
        />
        <div className="skeleton h-8 w-20 rounded-md" />
        <div className="skeleton h-3 w-24 rounded-md" />
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-5 flex flex-col items-center gap-1">
      {/* Label */}
      <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider text-center">
        {label}
      </p>

      {/* Gauge arc */}
      <div className="relative w-full" style={{ height: 130 }}>
        <ResponsiveContainer width="100%" height={130}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="100%"
              startAngle={180}
              endAngle={0}
              innerRadius="55%"
              outerRadius="80%"
              dataKey="value"
              strokeWidth={0}
              paddingAngle={0}
            >
              <Cell fill={gaugeColor} fillOpacity={0.9} />
              <Cell
                fill="currentColor"
                className="text-theme-muted"
                fillOpacity={0.12}
              />
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center overlay: percentage + target label */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none">
          <p
            className="text-3xl font-bold tabular-nums leading-none"
            style={{ color: gaugeColor }}
          >
            {pctDisplay}
          </p>
          <p className="text-xs text-theme-muted mt-1">
            Target: {Math.round(target * 100)}%
          </p>
        </div>
      </div>

      {/* Status badge */}
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1"
        style={{
          backgroundColor: `${gaugeColor}20`,
          color: gaugeColor,
        }}
      >
        {clamped >= target
          ? "On Target"
          : clamped >= target * 0.7
            ? "Near Target"
            : "Below Target"}
      </span>
    </div>
  );
}
